/**
 * <add-to-cart> — Unstyled Add-to-Cart Button Web Component
 *
 * Delegates cart mutations to <cart-drawer> via its public addItems() API.
 * The interaction is synchronous and server-confirmed:
 *   - Button shows "loading" while the request is in flight
 *   - The drawer opens immediately in its locked state (showing the last
 *     server-confirmed contents) and renders the new item on response
 *   - Button flips to "success" only after the server confirms the add
 *
 * Required attribute:
 *   variant-id      — Shopify variant ID
 *
 * Optional attributes:
 *   quantity        — defaults to 1
 *   data-price      — variant unit price in cents; lets the drawer project
 *                     the post-add subtotal so a 2ez-gift unlocked by this
 *                     add rides the same request (omit → gift arrives via
 *                     the app's reactive reconcile a beat later)
 *   no-drawer       — if present, don't auto-open the drawer after add
 *   sold-out        — if present, clicks are blocked and host reflects disabled state.
 *                     Toggle dynamically on variant change to react to inventory.
 *
 * State attribute (on host element):
 *   data-state="idle"      — default
 *   data-state="loading"   — request in flight
 *   data-state="success"   — item added (reverts to idle after 1.5s)
 *   data-state="error"     — add failed (reverts to idle after 3s)
 *   data-state="sold-out"  — variant unavailable; clicks are ignored
 */

class AddToCart extends HTMLElement {
  static get observedAttributes() {
    return ["sold-out"];
  }

  constructor() {
    super();
    this._ac = new AbortController();
    this._resetTimer = null;
  }

  connectedCallback() {
    this._syncSoldOut();

    this.addEventListener(
      "click",
      (e) => {
        const interactive = e.target.closest("a, button, [role='button']");
        if (!interactive && e.target !== this) return;
        e.preventDefault();
        this._add();
      },
      { signal: this._ac.signal },
    );
  }

  disconnectedCallback() {
    this._ac.abort();
    if (this._resetTimer) clearTimeout(this._resetTimer);
  }

  attributeChangedCallback(name) {
    if (name === "sold-out") this._syncSoldOut();
  }

  _syncSoldOut() {
    if (this.hasAttribute("sold-out")) {
      if (this._resetTimer) {
        clearTimeout(this._resetTimer);
        this._resetTimer = null;
      }
      this.dataset.state = "sold-out";
      this.setAttribute("aria-disabled", "true");
    } else {
      if (this.dataset.state === "sold-out" || !this.dataset.state) {
        this.dataset.state = "idle";
      }
      this.removeAttribute("aria-disabled");
    }
  }

  _add() {
    if (this.dataset.state === "loading") return;
    if (this.hasAttribute("sold-out")) return;

    const variantId = parseInt(this.getAttribute("variant-id"), 10);
    if (!variantId) return;

    const quantity = parseInt(this.getAttribute("quantity"), 10) || 1;
    const openDrawer = !this.hasAttribute("no-drawer");
    const price = parseInt(this.dataset.price, 10);

    const drawer = document.querySelector("cart-drawer");

    this.dataset.state = "loading";
    this.setAttribute("aria-disabled", "true");

    const add = drawer?.addItems
      ? drawer.addItems(
          [
            {
              id: variantId,
              quantity,
              price: Number.isFinite(price) ? price : undefined,
            },
          ],
          { openDrawer },
        )
      : this._fallbackAdd(variantId, quantity);

    add
      .then(() => this._setState("success", 1500))
      .catch(() => this._setState("error", 3000))
      .finally(() => this.removeAttribute("aria-disabled"));
  }

  /**
   * Fallback when <cart-drawer> is not on the page.
   * Posts directly to cart/add.js and dispatches cart:updated.
   */
  async _fallbackAdd(variantId, quantity) {
    const root =
      window.Shopify?.routes?.root || window.theme?.routes?.root || "/";
    const res = await fetch(root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: variantId, quantity }],
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.description || data.message || "Cart error");
    }
    /* Fetch updated cart and dispatch event */
    const cartRes = await fetch(root + "cart.js", {
      headers: { Accept: "application/json" },
    });
    const cart = await cartRes.json();
    document.dispatchEvent(
      new CustomEvent("cart:updated", { bubbles: true, detail: cart }),
    );
  }

  _setState(state, autoResetMs) {
    if (this._resetTimer) {
      clearTimeout(this._resetTimer);
      this._resetTimer = null;
    }
    this.dataset.state = state;
    if (autoResetMs) {
      this._resetTimer = setTimeout(() => {
        this.dataset.state = "idle";
        this._resetTimer = null;
      }, autoResetMs);
    }
  }
}

customElements.define("add-to-cart", AddToCart);
